import { IconChevronLeft, IconChevronRight, IconChevronsLeft, IconChevronsRight, IconDots } from "@tabler/icons-react";
import Link from "next/link";
import { cn } from "../../../lib/utils";
import { buttonVariants } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { CalendarCell } from "./calendar-cell";
import { CalendarDayNumber } from "./calendar-day-number";
import { CalendarEventItem } from "./calendar-event-item";
import type { CalendarEvent } from "./types";

const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export type CalendarProps = {
    className?: string;
    events: CalendarEvent[];
    /** Builds the navigation href for a given year and 1-based month. */
    hrefForMonth: (year: number, month: number) => string;
    /** 1-based month. */
    month: number;
    year: number;
};

function isoDate(year: number, month: number, day: number) {
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function daysInMonth(year: number, month: number) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

type GridCell = {
    date: string;
    day: number;
    muted: boolean;
};

function buildGridCells(year: number, month: number): GridCell[] {
    const dayCount = daysInMonth(year, month);
    const leadingCount = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
    const cellCount = Math.ceil((leadingCount + dayCount) / 7) * 7;

    const previousMonth = month === 1 ? 12 : month - 1;
    const previousYear = month === 1 ? year - 1 : year;
    const previousDayCount = daysInMonth(previousYear, previousMonth);

    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;

    const cells: GridCell[] = [];

    for (let index = 0; index < cellCount; index += 1) {
        const offset = index - leadingCount;

        if (offset < 0) {
            const day = previousDayCount + offset + 1;
            cells.push({ date: isoDate(previousYear, previousMonth, day), day, muted: true });
        } else if (offset < dayCount) {
            const day = offset + 1;
            cells.push({ date: isoDate(year, month, day), day, muted: false });
        } else {
            const day = offset - dayCount + 1;
            cells.push({ date: isoDate(nextYear, nextMonth, day), day, muted: true });
        }
    }

    return cells;
}

type MobileItem = { day: number; date: string; events: CalendarEvent[]; type: "day" } | { key: string; type: "gap" };

function buildMobileItems(year: number, month: number, eventsByDate: Map<string, CalendarEvent[]>): MobileItem[] {
    const dayCount = daysInMonth(year, month);
    const items: MobileItem[] = [];
    let inGap = false;

    for (let day = 1; day <= dayCount; day += 1) {
        const date = isoDate(year, month, day);
        const events = eventsByDate.get(date);

        if (events && events.length > 0) {
            items.push({ date, day, events, type: "day" });
            inGap = false;
        } else if (!inGap) {
            items.push({ key: `gap-${date}`, type: "gap" });
            inGap = true;
        }
    }

    return items;
}

function weekdayName(date: string) {
    return new Date(`${date}T00:00:00Z`).toLocaleDateString("en-US", { timeZone: "UTC", weekday: "long" });
}

export function Calendar({ className, events, hrefForMonth, month, year }: CalendarProps) {
    const eventsByDate = new Map<string, CalendarEvent[]>();
    for (const event of events) {
        const dateEvents = eventsByDate.get(event.date);
        if (dateEvents) {
            dateEvents.push(event);
        } else {
            eventsByDate.set(event.date, [event]);
        }
    }

    const cells = buildGridCells(year, month);
    const mobileItems = buildMobileItems(year, month, eventsByDate);

    const previousMonthHref = month === 1 ? hrefForMonth(year - 1, 12) : hrefForMonth(year, month - 1);
    const nextMonthHref = month === 12 ? hrefForMonth(year + 1, 1) : hrefForMonth(year, month + 1);

    const now = new Date();
    const todayHref = hrefForMonth(now.getFullYear(), now.getMonth() + 1);

    const monthLabel = new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-US", {
        month: "long",
        timeZone: "UTC",
        year: "numeric",
    });

    const navLinkClassName = buttonVariants({ size: "icon-sm", variant: "ghost" });

    return (
        <div className={cn("flex flex-col", className)}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-bold font-heading text-xl">{monthLabel}</h2>
                <div className="flex items-center gap-1">
                    <Link
                        aria-label="Previous year"
                        className={navLinkClassName}
                        href={hrefForMonth(year - 1, month)}
                        title="Previous year"
                    >
                        <IconChevronsLeft />
                    </Link>
                    <Link
                        aria-label="Previous month"
                        className={navLinkClassName}
                        href={previousMonthHref}
                        title="Previous month"
                    >
                        <IconChevronLeft />
                    </Link>
                    <Link className={buttonVariants({ size: "sm", variant: "outline" })} href={todayHref}>
                        Today
                    </Link>
                    <Link aria-label="Next month" className={navLinkClassName} href={nextMonthHref} title="Next month">
                        <IconChevronRight />
                    </Link>
                    <Link
                        aria-label="Next year"
                        className={navLinkClassName}
                        href={hrefForMonth(year + 1, month)}
                        title="Next year"
                    >
                        <IconChevronsRight />
                    </Link>
                </div>
            </div>

            <div className="mt-4 hidden min-h-0 flex-1 flex-col md:flex">
                <div className="grid grid-cols-7">
                    {weekdayLabels.map((label) => (
                        <div className="px-2 py-1.5 text-center font-medium text-muted-foreground text-xs" key={label}>
                            {label}
                        </div>
                    ))}
                </div>
                <Card className="overflow-hidden p-0">
                    <CardContent className="overflow-hidden p-0">
                        <div className="grid min-h-0 flex-1 auto-rows-[minmax(0,1fr)] grid-cols-7 divide-x divide-y divide-border bg-background">
                            {cells.map((cell) => (
                                <CalendarCell
                                    date={cell.date}
                                    day={cell.day}
                                    events={eventsByDate.get(cell.date) ?? []}
                                    key={cell.date}
                                    muted={cell.muted}
                                />
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>

            <ol className="mt-4 flex flex-col gap-2 md:hidden">
                {mobileItems.map((item) =>
                    item.type === "gap" ? (
                        <li aria-label="No events" className="flex justify-center py-1" key={item.key}>
                            <IconDots className="size-5 text-muted-foreground" />
                        </li>
                    ) : (
                        <li className="rounded-2xl border border-border p-3" key={item.date}>
                            <div className="flex items-center gap-2">
                                <CalendarDayNumber date={item.date} day={item.day} />
                                <span className="font-medium text-sm">{weekdayName(item.date)}</span>
                            </div>
                            <ul className="mt-2 flex flex-col gap-1">
                                {item.events.map((event) => (
                                    <li key={`${event.id}-${event.date}`}>
                                        <CalendarEventItem event={event} />
                                    </li>
                                ))}
                            </ul>
                        </li>
                    )
                )}
            </ol>
        </div>
    );
}
